import type { DailyCall } from '@daily-co/daily-js'
import { DailyProvider } from '@daily-co/daily-react'
import type { PendingStoreKiosk } from '@gozoki/api-types'
import { AuthContext, type AuthContextType } from '../utils/context/auth.context'
import {
  ClientEntryContext,
  type ClientEntryContextType,
} from '../utils/context/clientEntry.context'

import { type Right, hasOneRight, hasRights } from '@gozoki/tools'
import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  attachSocketToken,
  getCurrentAdminRbacPermissionsOptions,
  setAiSocketUrl,
  setGozokiApiWebSocketManager,
  setStreamSocketUrl,
  useAdminMe,
} from '@gozoki/api'
import { ThemeProvider } from '@mui/material'
import * as Sentry from '@sentry/react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { setToken } from '../utils/api'
import { VisioContext, type VisioContextType } from '../utils/context/visio.context'
import { materialTheme } from '../utils/theme/material.theme'

export interface GlobalProviderProps {
  children: React.ReactNode
}

const GlobalProvider = ({ children }: GlobalProviderProps) => {
  const me = useAdminMe()
  const rbacPermissions = useQuery({
    ...getCurrentAdminRbacPermissionsOptions(),
    enabled: !!me.data,
    retry: false,
  })
  const navigate = useNavigate()

  const [shouldStartCall, setShouldStartCall] = useState(false)
  const [kioskId, setKioskId] = useState<number | null>(null)
  const [storeId, setStoreId] = useState<number | null>(null)
  const [includeVideo, setIncludeVideo] = useState(false)
  const [openingEventId, setOpeningEventId] = useState<number | null>(null)
  const [isCallInProgress, setIsCallInProgress] = useState(false)
  const [callObject, setCallObject] = useState<null | DailyCall>(null)

  const [pendingKiosks, setPendingKiosks] = useState<PendingStoreKiosk[]>([])

  const askCall = useCallback(
    (
      newKioskId: number,
      newStoreId: number,
      newIncludeVideo: boolean,
      newOpeningEventId?: number,
    ) => {
      if (isCallInProgress || shouldStartCall) {
        return
      }

      setKioskId(newKioskId)
      setStoreId(newStoreId)
      setIncludeVideo(newIncludeVideo)
      setOpeningEventId(newOpeningEventId ?? null)
      setShouldStartCall(true)
    },
    [isCallInProgress, shouldStartCall],
  )

  const startCall = useCallback(() => {
    setShouldStartCall(false)
    setIsCallInProgress(true)
  }, [])

  const endCall = useCallback(() => {
    if (callObject) {
      callObject.leave()
      callObject.destroy()
      setCallObject(null)
    }
    setKioskId(null)
    setStoreId(null)
    setOpeningEventId(null)
    setIsCallInProgress(false)
  }, [callObject])

  const askEntry = useCallback((newPendingKiosk: PendingStoreKiosk) => {
    setPendingKiosks((prevKiosks) => [...prevKiosks, newPendingKiosk])
  }, [])

  const openClientForPendingKiosk = useCallback(
    (processedKioskId: number, openPage: boolean) => {
      setPendingKiosks((prevKiosks) => {
        const kioskToProcess = prevKiosks.find((kiosk) => kiosk.kioskId === processedKioskId)

        if (kioskToProcess && openPage) {
          navigate({
            search: { storeId: kioskToProcess.storeId },
            to: '/cameras',
          })
        }
        return prevKiosks.filter((kiosk) => kiosk.kioskId !== processedKioskId)
      })
    },
    [navigate],
  )

  useEffect(() => {
    if (me?.data?.socketToken) {
      attachSocketToken(me.data.socketToken)
    }
    if (me?.data?.socketAiUrl) {
      setAiSocketUrl(me.data.socketAiUrl)
    }
    if (me?.data?.socketStreamUrl) {
      setStreamSocketUrl(me.data.socketStreamUrl)
    }
    if (me?.data?.socketUrl) {
      setGozokiApiWebSocketManager(me.data.socketUrl)
    }
  }, [me])

  const logout = useCallback(async () => {
    await setToken(null)
    await me.refetch()
    Sentry.setUser(null)
    navigate({ to: '/login' }) // Navigate back to login page
  }, [me, navigate])

  const authContextValue: AuthContextType = useMemo(
    () => ({
      authenticated:
        !!me.data &&
        hasOneRight(me.data.adminRights, [
          'READER',
          'STOCK_READER',
          'ACCOUNTING_READER',
          'STORE_OPERATOR',
          'SUPPORT_OPERATOR',
          'ADMIN',
          'ACCOUNTING_ADMIN',
          'STOCK_ADMIN',
          'QUALITY',
          'REAL_ESTATE_READER',
          'REAL_ESTATE_ADMIN',
          'REAL_ESTATE_FINANCE_READER',
          'REAL_ESTATE_FINANCE_ADMIN',
        ]),
      user: me.data ?? null,
      refetchUser: me.refetch,
      isLoading: me.isLoading || (!!me.data && rbacPermissions.isLoading),
      logout,
      hasRights: (rights: Right[]) => !!me.data && hasRights(me.data.adminRights, rights),
      hasOneRight: (rights: Right[]) => !!me.data && hasOneRight(me.data.adminRights, rights),
      rbacEnforced: rbacPermissions.data?.enforcementEnabled ?? false,
      rbacLoading: !!me.data && rbacPermissions.isLoading,
      hasResourcePermission: (resource, permissionType) => {
        if (!(rbacPermissions.data?.enforcementEnabled ?? false)) {
          return true
        }

        return (
          rbacPermissions.data?.grants.some(
            (grant) => grant.resource === resource && grant.permissionType === permissionType,
          ) ?? false
        )
      },
    }),
    [me.data, me.refetch, me.isLoading, logout, rbacPermissions.data, rbacPermissions.isLoading],
  )

  const visioContextValue: VisioContextType = useMemo(
    () => ({
      shouldStartCall,
      kioskId,
      storeId,
      includeVideo,
      openingEventId,
      isCallInProgress,
      callObject,
      setCallObject,
      askCall,
      startCall,
      endCall,
    }),
    [
      kioskId,
      shouldStartCall,
      storeId,
      includeVideo,
      openingEventId,
      isCallInProgress,
      callObject,
      setCallObject,
      endCall,
      startCall,
      askCall,
    ],
  )

  const clientEntryContextValue: ClientEntryContextType = useMemo(
    () => ({
      pendingKiosks,
      askEntry,
      openClientForPendingKiosk,
    }),
    [pendingKiosks, askEntry, openClientForPendingKiosk],
  )

  return (
    <AuthContext.Provider value={authContextValue}>
      <VisioContext.Provider value={visioContextValue}>
        <DailyProvider callObject={callObject}>
          <ClientEntryContext.Provider value={clientEntryContextValue}>
            <ThemeProvider theme={materialTheme}>{children}</ThemeProvider>
          </ClientEntryContext.Provider>
        </DailyProvider>
      </VisioContext.Provider>
    </AuthContext.Provider>
  )
}

export default GlobalProvider
